import { useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@rudi/ui/button';
import { PendingDialog } from './PendingTenantsPage';
import type { PendingTenant } from '../api/types';
import { Link, useNavigate } from '@tanstack/react-router';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { Badge } from '@rudi/ui/badge';
import { QueryState } from '../components/page';
import {
  useTenants,
  usePendingTenants,
  useDeletePendingTenant,
  useProvisionPendingTenant,
} from '../api/hooks';

export function TenantsPage() {
  const { data, isLoading, error } = useTenants();

  const pending = usePendingTenants(false);
  const del = useDeletePendingTenant();
  const provision = useProvisionPendingTenant();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<{ initial: PendingTenant | null } | null>(null);
  const prepare = async (id: string) => {
    try {
      const tenant = await provision.mutateAsync(id);
      await navigate({ to: '/tenants/$tenantId', params: { tenantId: tenant.id } });
      toast.success('Tenant prepared. You can now create teams.');
    } catch (error) {
      const data = (error as { response?: { data?: { error?: string; detail?: string } } }).response
        ?.data;
      toast.error(data?.error ?? data?.detail ?? 'Could not prepare tenant');
    }
  };
  const onDelete = async (id: string) => {
    if (!confirm('Delete this pending tenant?')) return;
    try {
      await del.mutateAsync(id);
      toast.success('Pending tenant deleted');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Delete failed');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight">Tenants</h1>
          <Button
            size="icon"
            variant="outline"
            aria-label="Add tenant"
            title="Add tenant"
            onClick={() => setDialog({ initial: null })}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage organizations, pending sign-ups, and team preparation.
        </p>
      </div>
      <QueryState
        isLoading={isLoading || pending.isLoading}
        error={error || pending.error}
        empty={data?.tenants.length === 0 && pending.data?.pending_tenants.length === 0}
      >
        <div className="rounded-3xl border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Members</TableHead>
                <TableHead>Admins</TableHead>
                <TableHead>Domains</TableHead>
                <TableHead>
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.tenants.map(t => (
                <TableRow key={t.id}>
                  <TableCell className="font-medium">
                    <Link
                      to="/tenants/$tenantId"
                      params={{ tenantId: t.id }}
                      className="hover:underline"
                    >
                      {t.name}
                    </Link>
                    {t.is_active && t.member_count === 0 && (
                      <Badge variant="secondary" className="ml-2">
                        Awaiting sign-ups
                      </Badge>
                    )}
                    {!t.is_active && (
                      <Badge variant="secondary" className="ml-2">
                        inactive
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{t.slug ?? '—'}</TableCell>
                  <TableCell>{t.member_count}</TableCell>
                  <TableCell>
                    {t.tenant_admin_count === 0 ? (
                      <Badge variant="destructive">no admin</Badge>
                    ) : (
                      t.tenant_admin_count
                    )}
                  </TableCell>
                  <TableCell className="max-w-[18rem] truncate text-muted-foreground">
                    {t.allowed_email_domains.join(', ') || '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Link
                      to="/tenants/$tenantId"
                      params={{ tenantId: t.id }}
                      className="text-sm hover:underline"
                    >
                      Manage
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
              {pending.data?.pending_tenants.map(p => (
                <TableRow key={`pending-${p.id}`}>
                  <TableCell className="font-medium">
                    {p.name}
                    <Badge variant="secondary" className="ml-2">
                      Pending
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{p.slug ?? '—'}</TableCell>
                  <TableCell>0</TableCell>
                  <TableCell className="text-muted-foreground">—</TableCell>
                  <TableCell className="max-w-[18rem] truncate text-muted-foreground">
                    {p.allowed_email_domains.join(', ')}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        disabled={provision.isPending || del.isPending}
                        onClick={() => void prepare(p.id)}
                      >
                        Prepare teams
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={provision.isPending || del.isPending}
                        onClick={() => setDialog({ initial: p })}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={provision.isPending || del.isPending}
                        onClick={() => void onDelete(p.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </QueryState>
      {dialog && <PendingDialog initial={dialog.initial} onClose={() => setDialog(null)} />}
    </div>
  );
}
